import React, { useState, useEffect } from 'react';
import { teamApi, projectApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users,
  Plus,
  MoreVertical,
  Mail,
  Shield,
  HardHat,
  UserCog,
  Trash2,
  FolderPlus,
  Copy,
  Check,
  Key,
  Eye,
  BarChart3,
  Calendar,
  Target,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Team() {
  const { user } = useAuth();
  const userRole = user?.role || 'owner';
  const isOwner = userRole === 'owner';
  const isManager = userRole === 'manager';
  const [members, setMembers] = useState([]);
  const [showMemberDashboard, setShowMemberDashboard] = useState(false);
  const [selectedMemberDashboard, setSelectedMemberDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [newMemberCredentials, setNewMemberCredentials] = useState(null);
  // Default role for managers is foreperson (they can only invite forepersons)
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: isManager ? 'foreperson' : 'manager' });
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, projectsRes] = await Promise.all([
        teamApi.getAll(),
        projectApi.getAll(),
      ]);
      setMembers(membersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load team data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.email || !inviteData.name) {
      toast.error('Please fill in all fields');
      return;
    }
    setInviting(true);
    try {
      const response = await teamApi.invite(inviteData);
      setShowInviteDialog(false);
      
      // Check if temp_password was returned (Gmail not connected)
      if (response.data.temp_password) {
        setNewMemberCredentials({
          email: inviteData.email,
          name: inviteData.name,
          password: response.data.temp_password,
        });
        setShowCredentialsDialog(true);
      } else {
        toast.success('Invitation email sent to ' + inviteData.email);
      }
      
      setInviteData({ email: '', name: '', role: 'manager' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const copyCredentials = async () => {
    if (newMemberCredentials) {
      const text = `Email: ${newMemberCredentials.email}\nTemporary Password: ${newMemberCredentials.password}`;
      
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          toast.success('Credentials copied to clipboard');
        } else {
          throw new Error('Clipboard API not available');
        }
      } catch (err) {
        // Fallback
        try {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success('Credentials copied to clipboard');
          } else {
            toast.info('Please copy manually: ' + newMemberCredentials.email, { duration: 10000 });
          }
        } catch (fallbackErr) {
          toast.info('Please copy manually: ' + newMemberCredentials.email, { duration: 10000 });
        }
      }
    }
  };

  const handleAssignProject = async (memberId, projectId) => {
    try {
      await teamApi.assignProject(memberId, projectId);
      fetchData();
      toast.success('Project assigned');
    } catch (error) {
      toast.error('Failed to assign project');
    }
  };

  const handleRemove = async (memberId) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await teamApi.remove(memberId);
      // Use functional update to avoid stale closure
      setMembers(prevMembers => prevMembers.filter(m => m.id !== memberId));
      toast.success('Team member removed');
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleResetPassword = async (member) => {
    if (!window.confirm(`Send password reset email to ${member.email}?`)) return;
    try {
      await teamApi.resetPassword(member.id);
      toast.success(`Password reset email sent to ${member.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const handleViewDashboard = async (member) => {
    setLoadingDashboard(true);
    try {
      const response = await teamApi.getMemberDashboard(member.id);
      setSelectedMemberDashboard(response.data);
      setShowMemberDashboard(true);
    } catch (error) {
      toast.error('Failed to load member dashboard');
    } finally {
      setLoadingDashboard(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'manager': return UserCog;
      case 'foreperson': return HardHat;
      default: return Shield;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'manager': return 'bg-blue-600';
      case 'foreperson': return 'bg-orange-500';
      default: return 'bg-slate-900';
    }
  };

  // Check if user can remove a specific member
  const canRemoveMember = (member) => {
    if (member.role === 'owner') return false; // The account owner can never be removed
    if (isOwner) return true; // Owners can remove anyone
    if (isManager && member.role === 'foreperson') return true; // Managers can remove forepersons
    return false;
  };

  // Check if user has access to team page
  if (userRole !== 'owner' && userRole !== 'manager') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md rounded-sm">
          <CardContent className="p-8 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">Only account owners and managers can view the team page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team</h1>
          <p className="text-muted-foreground">Manage your team members and their access</p>
        </div>
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="invite-btn">
              <Plus className="h-4 w-4 mr-2" />
              Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  placeholder="John Smith"
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={inviteData.role}
                  onValueChange={(val) => setInviteData({ ...inviteData, role: val })}
                  disabled={isManager} // Managers can't change role - fixed to foreperson
                >
                  <SelectTrigger className="rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Only owners can invite managers */}
                    {isOwner && (
                      <SelectItem value="manager">
                        <div className="flex items-center gap-2">
                          <UserCog className="h-4 w-4" />
                          Project Manager
                        </div>
                      </SelectItem>
                    )}
                    <SelectItem value="foreperson">
                      <div className="flex items-center gap-2">
                        <HardHat className="h-4 w-4" />
                        Foreperson
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  {inviteData.role === 'manager' 
                    ? 'Managers can view/edit their assigned projects and see financials'
                    : 'Forepersons can only view tasks on assigned projects'}
                  {isManager && <><br /><em>As a manager, you can only invite forepersons.</em></>}
                </p>
              </div>
              <Button onClick={handleInvite} disabled={inviting} className="w-full rounded-sm">
                {inviting ? 'Inviting...' : 'Send Invitation'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-slate-900" />
          <span><strong>Owner</strong> - Full access to everything</span>
        </div>
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4 text-blue-600" />
          <span><strong>Manager</strong> - Projects, phases, financials (assigned)</span>
        </div>
        <div className="flex items-center gap-2">
          <HardHat className="h-4 w-4 text-orange-500" />
          <span><strong>Foreperson</strong> - View tasks only</span>
        </div>
      </div>

      {/* Team Members Grid */}
      {members.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="py-16 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No team members yet</h3>
            <p className="text-muted-foreground mb-4">Invite project managers and forepersons to collaborate</p>
            <Button onClick={() => setShowInviteDialog(true)} className="rounded-sm">
              Invite Your First Team Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((member) => {
            const RoleIcon = getRoleIcon(member.role);
            const isOwnerRow = member.role === 'owner';
            const isSelf = member.id === user?.id;
            return (
              <Card key={member.id} className="rounded-sm" data-testid={`team-member-${member.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-sm bg-slate-900 flex items-center justify-center text-white font-bold text-lg">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {member.name}
                          {isSelf && <Badge variant="outline" className="text-[10px] px-1.5 py-0">You</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {member.email}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isOwner && !isOwnerRow && (
                          <>
                            <DropdownMenuItem 
                              onClick={() => handleViewDashboard(member)}
                              data-testid={`view-dashboard-${member.id}`}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Dashboard
                            </DropdownMenuItem>
                            {member.auth_provider !== 'google' && (
                              <DropdownMenuItem 
                                onClick={() => handleResetPassword(member)}
                                data-testid={`reset-password-${member.id}`}
                              >
                                <Key className="h-4 w-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                          </>
                        )}
                        {isOwnerRow ? (
                          <DropdownMenuItem disabled className="text-muted-foreground">
                            <Shield className="h-4 w-4 mr-2" />
                            Account owner
                          </DropdownMenuItem>
                        ) : canRemoveMember(member) ? (
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleRemove(member.id)}
                            data-testid={`remove-member-${member.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem disabled className="text-muted-foreground">
                            <Shield className="h-4 w-4 mr-2" />
                            Cannot remove {member.role}s
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  {/* Clickable name/avatar to view dashboard */}
                  {isOwner && !isOwnerRow && (
                    <div 
                      className="cursor-pointer hover:bg-secondary/30 -mx-4 -mt-3 px-4 pt-3 pb-2 transition-colors"
                      onClick={() => handleViewDashboard(member)}
                    >
                      <p className="text-xs text-muted-foreground">Click to view dashboard</p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={getRoleBadgeColor(member.role)}>
                      <RoleIcon className="h-3 w-3 mr-1" />
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </Badge>
                    <Badge variant={member.status === 'active' ? 'default' : 'secondary'} className={member.status === 'active' ? 'bg-green-600' : ''}>
                      {member.status}
                    </Badge>
                  </div>

                  {/* Assigned Projects - Only owners can assign projects */}
                  {isOwnerRow ? (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-muted-foreground italic">Full access to all leads and projects</p>
                    </div>
                  ) : (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Assigned Projects ({member.assigned_projects?.length || 0})</p>
                      {isOwner ? (
                        <Select onValueChange={(projectId) => handleAssignProject(member.id, projectId)}>
                          <SelectTrigger className="h-8 text-xs rounded-sm">
                            <FolderPlus className="h-3 w-3 mr-1" />
                            <SelectValue placeholder="Assign project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Only owners can assign projects</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Credentials Dialog - shown when Gmail not connected */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-orange-500" />
              Team Member Credentials
            </DialogTitle>
          </DialogHeader>
          {newMemberCredentials && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-sm">
                <p className="text-sm text-yellow-800 mb-2">
                  <strong>Important:</strong> Gmail is not connected, so we couldn't send an invitation email. 
                  Please share these credentials with {newMemberCredentials.name} manually.
                </p>
              </div>
              
              <div className="p-4 bg-slate-100 rounded-sm space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-mono text-sm">{newMemberCredentials.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Temporary Password</Label>
                  <p className="font-mono text-lg font-bold text-orange-600">{newMemberCredentials.password}</p>
                </div>
              </div>

              <Button
                onClick={copyCredentials}
                className="w-full rounded-sm"
                variant={copied ? "secondary" : "default"}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Credentials
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                The team member should change their password after first login.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Member Dashboard Dialog */}
      <Dialog open={showMemberDashboard} onOpenChange={setShowMemberDashboard}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              {selectedMemberDashboard?.member?.name}'s Dashboard
            </DialogTitle>
          </DialogHeader>
          {selectedMemberDashboard && (
            <div className="space-y-4 mt-4">
              {/* Member Info */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-sm">
                <div className="h-12 w-12 rounded-sm bg-slate-900 dark:bg-slate-700 flex items-center justify-center text-white font-bold text-lg">
                  {selectedMemberDashboard.member.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold">{selectedMemberDashboard.member.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedMemberDashboard.member.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={getRoleBadgeColor(selectedMemberDashboard.member.role)}>
                      {selectedMemberDashboard.member.role}
                    </Badge>
                    <Badge variant={selectedMemberDashboard.member.status === 'active' ? 'default' : 'secondary'} className={selectedMemberDashboard.member.status === 'active' ? 'bg-green-600' : ''}>
                      {selectedMemberDashboard.member.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border rounded-sm">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Target className="h-4 w-4" />
                    <span className="text-xs">Assigned Leads</span>
                  </div>
                  <p className="text-2xl font-bold">{selectedMemberDashboard.leads.total}</p>
                  <p className="text-xs text-muted-foreground">
                    ${(selectedMemberDashboard.leads.total_value || 0).toLocaleString()} total value
                  </p>
                </div>
                <div className="p-4 border rounded-sm">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <FolderPlus className="h-4 w-4" />
                    <span className="text-xs">Assigned Projects</span>
                  </div>
                  <p className="text-2xl font-bold">{selectedMemberDashboard.projects.total}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedMemberDashboard.projects.active} active, {selectedMemberDashboard.projects.completed} completed
                  </p>
                </div>
              </div>

              {/* Lead Stages */}
              {Object.keys(selectedMemberDashboard.leads.by_stage || {}).length > 0 && (
                <div className="p-4 border rounded-sm">
                  <p className="text-sm font-medium mb-2">Leads by Stage</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedMemberDashboard.leads.by_stage).map(([stage, count]) => (
                      <Badge key={stage} variant="outline">
                        {stage}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {selectedMemberDashboard.recent_activities?.length > 0 && (
                <div className="p-4 border rounded-sm">
                  <p className="text-sm font-medium mb-2">Recent Activity</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedMemberDashboard.recent_activities.map((activity, idx) => (
                      <div key={activity.id || `activity-${idx}`} className="text-xs p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <p>{activity.description}</p>
                        <p className="text-muted-foreground">
                          {new Date(activity.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Events */}
              {selectedMemberDashboard.upcoming_events?.length > 0 && (
                <div className="p-4 border rounded-sm">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Upcoming Events
                  </p>
                  <div className="space-y-2">
                    {selectedMemberDashboard.upcoming_events.map((event, idx) => (
                      <div key={event.id || `event-${idx}`} className="text-xs flex justify-between">
                        <span>{event.title}</span>
                        <span className="text-muted-foreground">
                          {new Date(event.start_time).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
